import { useState, type FormEvent } from "react"
import { toast } from "sonner"
import { Input } from "@/components/ui/input"
import { usePendingAction } from "@/hooks/use-pending-action"
import type { Id } from "../../../../convex/_generated/dataModel"
import { useReferSomeone } from "../data/use-refer-someone"
import { cn } from "@/lib/utils"

export function ReferSomeonePanel({
  jobId,
  jobTitle,
  variant = "panel",
}: {
  jobId?: Id<"jobs">
  jobTitle?: string
  variant?: "panel" | "band"
}) {
  const { refer } = useReferSomeone()
  const [email, setEmail] = useState("")
  const { pending, run } = usePendingAction()
  const isBand = variant === "band"

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) return
    const ok = await run(
      async () => {
        const result = await refer({
          email: trimmed,
          ...(jobId ? { jobId } : {}),
        })
        if (result.outcome === "already_referred") {
          toast.message("You've already invited this person")
        } else {
          toast.success("Invite sent")
        }
      },
      { error: "Failed to send invite" },
    )
    if (ok) setEmail("")
  }

  const title = "Refer someone"
  const copy = jobTitle
    ? `Know someone who'd fit ${jobTitle}? We'll email them a link to this role.`
    : "Send a friend an email to browse open HSR roles and apply on Hire."

  return (
    <div
      className={cn(
        isBand ? "mkt-job-band mkt-job-refer mkt-form" : "mkt-panel mkt-form",
      )}
    >
      {isBand ? (
        <h2>{title}</h2>
      ) : (
        <p className="mkt-panel__title">{title}</p>
      )}
      <p className={isBand ? undefined : "mkt-panel__copy"}>{copy}</p>
      <form
        className={isBand ? "mkt-job-refer__form" : "mt-3 space-y-2"}
        onSubmit={onSubmit}
      >
        <div className={isBand ? "mkt-job-refer__field" : undefined}>
          <label htmlFor="job-referral-email">Email</label>
          <Input
            id="job-referral-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder="friend@email.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            disabled={pending}
          />
        </div>
        <button
          type="submit"
          className={
            isBand
              ? "home-wf-btn home-wf-btn--lime mkt-job-refer__cta"
              : "home-wf-btn home-wf-btn--lime mkt-panel__cta"
          }
          disabled={pending}
        >
          {pending ? "Sending…" : "Send invite"}
        </button>
      </form>
    </div>
  )
}
